import React, { useState } from 'react';
import Head from 'src/layout/head/Head';
import {
  PencilSquareIcon,
  CheckIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

const defaultContent = `# About Our Organisation

We are a leading travel company that uses Felloh to manage and process payments across our business. Our team handles bookings for holidays, flights, and travel packages for customers across the UK and Europe.

## Key Information

- **Industry:** Travel & Tourism
- **Regions:** United Kingdom, Europe
- **Payment Methods:** Credit/Debit Cards, Bank Transfers, Open Banking
- **Booking Volume:** Approximately 2,000 bookings per month

## Customer Communication Guidelines

When communicating with customers about payments, we should always:

1. Use clear, friendly language that avoids overly technical jargon
2. Reference booking numbers in all correspondence
3. Provide transparent breakdowns of costs including any surcharges
4. Offer multiple payment options where possible
5. Follow up on failed payments within 24 hours

## Refund Policy

Our standard refund policy allows customers to request a full refund within 14 days of booking, provided the travel date is more than 30 days away. Partial refunds are available on a case-by-case basis for cancellations made within the 30-day window.

## Compliance Notes

All payment processing must comply with PCI DSS requirements. Customer card details should never be stored locally or shared via email. All sensitive payment data is handled through Felloh's secure infrastructure.
`;

const renderMarkdown = (text) => {
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Headers
  html = html.replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold text-gray-900 dark:text-white mt-5 mb-2">$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2 class="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2">$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold text-gray-900 dark:text-white mt-6 mb-3">$1</h1>');

  // Bold and italic
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Unordered lists
  html = html.replace(/^- (.+)$/gm, '<li class="ml-4 list-disc text-sm text-gray-700 dark:text-gray-300">$1</li>');

  // Ordered lists
  html = html.replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal text-sm text-gray-700 dark:text-gray-300">$1</li>');

  // Paragraphs - wrap lines that aren't already wrapped in tags
  html = html.replace(/^(?!<[hlu]|<li|<strong|<em)(.+)$/gm, (_match, content) => {
    if (content.trim() === '') return '';
    return `<p class="text-sm text-gray-700 dark:text-gray-300 mb-2">${content}</p>`;
  });

  // Clean up empty lines
  html = html.replace(/^\s*$/gm, '');

  return html;
};

const PlaybooksContext = () => {
  const [content, setContent] = useState(defaultContent);
  const [mode, setMode] = useState('preview');

  return (
    <React.Fragment>
      <Head title="Shared Context" />
      <div className="w-full">
        <div className="mb-4 flex items-center justify-between">
          <div />
          <div className="flex items-center gap-2">
            {mode === 'edit' ? (
              <button
                onClick={() => setMode('preview')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors bg-violet-800 text-white hover:bg-violet-600"
              >
                <CheckIcon className="w-4 h-4" />
                Save
              </button>
            ) : (
              <button
                onClick={() => setMode('edit')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors bg-violet-800 text-white hover:bg-violet-600"
              >
                <PencilSquareIcon className="w-4 h-4" />
                Edit
              </button>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-violet-800 rounded-lg clearfix">
          {mode === 'edit' ? (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="block w-full h-[600px] px-6 py-5 bg-transparent text-gray-900 dark:text-white font-mono text-sm focus:outline-none resize-none"
              placeholder="Write your organisation context in markdown..."
            />
          ) : (
            <div className="px-6 py-2">
              {content.trim() ? (
                <div dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }} />
              ) : (
                <div className="text-center py-12">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No context added</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Switch to edit mode to add context about your organisation.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default PlaybooksContext;
